import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Upload, Trash2, Loader2, Copy, Check, Image as ImageIcon, Video } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { absUrl } from "@/components/admin/UploadWidget";

export default function AdminMedia() {
    useSEO({ title: "Media Library — Admin | RKS Systems" });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState("");
    const [filter, setFilter] = useState("all");
    const fileRef = useRef(null);

    const load = () => {
        setLoading(true);
        const params = filter === "all" ? {} : { kind: filter };
        api.get("/admin/media", { params }).then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            load();
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this file permanently?")) return;
        try { await api.delete(`/admin/media/${id}`); load(); } catch (e) { alert(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
    };

    const copyUrl = (url) => {
        navigator.clipboard?.writeText(url);
        setCopied(url);
        setTimeout(() => setCopied(""), 1500);
    };

    const fmtBytes = (n) => {
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div className="p-8" data-testid="page-admin-media">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/admin/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ChevronLeft className="h-3 w-3" /> Back to dashboard</Link>
                    <h1 className="mt-1 font-heading text-3xl font-bold">Media Library</h1>
                    <p className="text-muted-foreground mt-1 text-sm">Upload and reuse images and videos across the website. Paste the copied URL into any content field.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 rounded-md border border-input bg-white p-1">
                        {["all", "image", "video"].map((k) => (
                            <button key={k} onClick={() => setFilter(k)} data-testid={`media-filter-${k}`} className={`px-3 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-colors ${filter === k ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>{k}</button>
                        ))}
                    </div>
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-btn" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploading ? "Uploading..." : "Upload"}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" data-testid="media-file-input" onChange={(e) => upload(e.target.files?.[0])} />
                </div>
            </div>

            {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            {loading ? (
                <div className="mt-12 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
            ) : items.length === 0 ? (
                <div className="mt-12 rounded-xl border border-dashed border-input p-16 text-center text-muted-foreground" data-testid="media-empty">
                    <ImageIcon className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
                    <p>No media uploaded yet.</p>
                    <button onClick={() => fileRef.current?.click()} className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"><Upload className="h-4 w-4" /> Upload your first file</button>
                </div>
            ) : (
                <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((m) => (
                        <div key={m.id} data-testid={`media-item-${m.id}`} className="group rounded-xl border border-border bg-white overflow-hidden">
                            <div className="aspect-video bg-secondary/40 flex items-center justify-center">
                                {m.kind === "image" ? (
                                    <img src={absUrl(m.url)} alt={m.original_name} className="max-h-full max-w-full object-contain" />
                                ) : (
                                    <div className="flex flex-col items-center text-muted-foreground">
                                        <Video className="h-8 w-8" />
                                        <span className="mt-1 text-[10px] uppercase tracking-widest">video</span>
                                    </div>
                                )}
                            </div>
                            <div className="p-3">
                                <div className="text-xs font-semibold truncate text-foreground" title={m.original_name}>{m.original_name}</div>
                                <div className="mt-0.5 text-[10px] text-muted-foreground flex items-center gap-2">
                                    <span className="uppercase font-semibold tracking-wider">{m.kind}</span>
                                    <span>·</span>
                                    <span>{fmtBytes(m.size_bytes)}</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <button onClick={() => copyUrl(m.url)} data-testid={`media-copy-${m.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-input px-2.5 py-1.5 text-[11px] font-semibold hover:bg-secondary">
                                        {copied === m.url ? <><Check className="h-3 w-3 text-green-600" /> Copied</> : <><Copy className="h-3 w-3" /> Copy URL</>}
                                    </button>
                                    <button onClick={() => del(m.id)} data-testid={`media-delete-${m.id}`} className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-input text-muted-foreground hover:text-red-600 hover:border-red-200">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
